import type { HelpContent } from './types';

export const projectTemplatesHelp: HelpContent = {
  pageTitle: 'Project Templates',
  description: 'Create and manage reusable project templates to speed up project creation and ensure consistency across your team.',

  functionality: {
    overview:
      'The Project Templates page lets you define reusable templates that can be applied when creating new projects. Each template captures default settings, structure, and configuration so you don\'t have to start from scratch every time. Templates can be organised by category, marked as favourites, and searched or filtered to find what you need quickly.',
    keyFeatures: [
      {
        label: 'Template library',
        detail: 'Browse all available templates in a card-based layout. Filter by category, search by name, or sort by most used, recently created, recently used, or alphabetically.',
      },
      {
        label: 'Create and edit templates',
        detail: 'Create new templates from scratch or edit existing ones. Capture default settings such as category, priority, type, duration, cost centres, and resource budgets.',
      },
      {
        label: 'Use a template',
        detail: 'Apply any template directly from the template card to pre-populate a new project with the template\'s default settings, saving time and reducing manual entry.',
      },
      {
        label: 'Duplicate a template',
        detail: 'Quickly create a copy of an existing template as a starting point for a new variation. The duplicate opens in the edit form ready for you to rename and adjust.',
      },
      {
        label: 'Favourites',
        detail: 'Mark frequently used templates as favourites using the star icon. Filter the view to show only your favourites for faster access.',
      },
      {
        label: 'Category filters',
        detail: 'Templates are organised by category (e.g. Consulting, Fixed-Fee Delivery, Infrastructure). Use the filter pills to narrow the list to a specific category.',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Search bar',
        description: 'Search templates by name or description. Results update as you type.',
      },
      {
        name: 'Sort control',
        description: 'Sort the template list by most used, recently created, recently used, A–Z, or Z–A.',
      },
      {
        name: 'Category filter pills',
        description: 'Filter templates by category. Each pill shows the number of templates in that category. Select "Favourites" to show only starred templates.',
      },
      {
        name: 'Template card',
        description: 'Each card displays the template name, description, category, priority, type, usage count, default duration, cost centres, resource budget summary, and when it was last used.',
      },
      {
        name: 'Use Template button',
        description: 'Applies the template to a new project, opening the project creation panel pre-populated with the template\'s default settings.',
      },
      {
        name: 'Edit button',
        description: 'Opens the template in the edit form so you can update its settings.',
      },
      {
        name: 'Duplicate button',
        description: 'Creates a copy of the template and opens it in the create form, ready to be renamed and saved as a new template.',
      },
      {
        name: 'Delete button',
        description: 'Permanently removes the template. A confirmation step is shown before deletion to prevent accidental removal.',
      },
      {
        name: 'Favourite star',
        description: 'Toggle the star icon on any template card to add or remove it from your favourites.',
      },
      {
        name: 'New Template button',
        description: 'Opens the template creation form to define a new template from scratch.',
      },
    ],
    useCases: [
      'Setting up a standard template for a common project type to ensure consistency',
      'Creating a new project quickly by applying a pre-configured template',
      'Managing a library of templates for different clients, categories, or delivery types',
      'Duplicating an existing template to create a variation without starting from scratch',
    ],
  },

  userGuide: {
    gettingStarted:
      'Navigate to Project Templates from the left sidebar. You will see all available templates displayed as cards. Use the search bar, category filter pills, and sort control to find what you need. Click "New Template" to create your first template.',
    steps: [
      {
        title: 'How do I create a new template?',
        items: [
          {
            text: 'Click the "New Template" button in the top-right of the page.',
          },
          {
            text: 'Fill in the template details — name, description, category, priority, type, default duration, and any other relevant settings.',
          },
          {
            text: 'Save the template. It will appear in the template library immediately.',
          },
        ],
      },
      {
        title: 'How do I use a template to create a project?',
        items: [
          {
            text: 'Find the template you want to use in the library.',
          },
          {
            text: 'Click the "Use Template" button on the template card.',
          },
          {
            text: 'The project creation panel will open pre-populated with the template\'s default settings. Review and adjust as needed, then save.',
          },
        ],
      },
      {
        title: 'How do I duplicate a template?',
        items: [
          {
            text: 'Find the template you want to duplicate and click the "Duplicate" button on its card.',
          },
          {
            text: 'The template form opens in create mode with the duplicated data pre-filled and the name suffixed with "(Copy)".',
          },
          {
            text: 'Update the name and any other details, then save.',
          },
        ],
      },
      {
        title: 'How do I delete a template?',
        items: [
          {
            text: 'Click the "Delete" button on the template card.',
          },
          {
            text: 'A confirmation prompt will appear. Click "Confirm Delete" to permanently remove the template.',
            note: 'Deleted templates cannot be recovered.',
          },
        ],
      },
    ],
    proTips: [
      'Star your most frequently used templates so you can filter to just your favourites and find them instantly.',
      'Use the "Duplicate" feature to create category-specific variations of a base template without rebuilding from scratch each time.',
      'Add a clear description to each template — it appears on the card and makes it easier for your team to identify the right template quickly.',
    ],
  },

  faq: [
    {
      question: 'Can I edit a template after it has been used to create a project?',
      answer: 'Yes. Editing a template only affects future projects created from it — it does not change any projects that have already been created using that template.',
    },
    {
      question: 'Why does my template not appear in the list?',
      answer: 'Check whether a category filter or search term is active. Clear any filters using the "All" pill or clear the search bar to see all templates.',
    },
    {
      question: 'Can I delete a template that has been used to create projects?',
      answer: 'Yes. Deleting a template does not affect any projects that were created from it. Only the template itself is removed.',
    },
    {
      question: 'What happens to the usage count on a template?',
      answer: 'The usage count increments each time the template is used to create a new project. It helps you identify which templates are most popular across your team.',
    },
    {
      question: 'Can I share templates with other users?',
      answer: 'Templates are available to all users who have access to the Project Templates page. There is no need to share them individually.',
    },
  ],

  relatedPages: [
    { label: 'Projects', path: '/projects' },
  ],
};
